export interface AppointmentOutcomeCounts {
  scheduled: number;
  honored: number;
  noShow: number;
  cancelledByPatient: number;
  cancelledByHospital: number;
  upcoming?: number;
  noShowRate?: number;
}

export interface AppointmentOutcomeDepartmentRow extends AppointmentOutcomeCounts {
  departmentUuid: string;
  departmentName: string;
  noShowRate: number;
}

export interface AppointmentOutcomeStatsResponse {
  status: number;
  stats: {
    totals: AppointmentOutcomeCounts & { noShowRate: number };
    byDepartment: AppointmentOutcomeDepartmentRow[];
    chart: {
      categories: string[];
      series: Array<{ name: string; data: number[] }>;
    };
  };
}

export function computeNoShowRate(counts: Pick<
  AppointmentOutcomeCounts,
  'noShow' | 'scheduled' | 'cancelledByPatient' | 'cancelledByHospital'
>): number {
  const denominator = counts.scheduled - counts.cancelledByPatient - counts.cancelledByHospital;
  if (denominator <= 0) {
    return 0;
  }
  return Number(((counts.noShow / denominator) * 100).toFixed(1));
}

export const OUTCOME_CHART_SERIES_KEYS = [
  'honored',
  'noShow',
  'cancelledByPatient',
  'cancelledByHospital',
] as const;

export function buildOutcomeChartSeries(
  stats: AppointmentOutcomeStatsResponse['stats'],
  translate: (key: string) => string,
) {
  const colorByKey: Record<string, string> = {
    honored: '#43A047',
    noShow: '#E53935',
    cancelledByPatient: '#FFB300',
    cancelledByHospital: '#607D8B',
  };

  const counts = OUTCOME_CHART_SERIES_KEYS.map(
    (key) => stats.chart.series.find((series) => series.name === key)?.data || [],
  );

  // Plotted as raw counts, the chart answered "which service is biggest" instead of
  // "which service has a problem": the busiest one drew the tallest bar of every colour,
  // and a small service losing 40% of its appointments to no-shows sat invisible next to
  // it. Each service is normalised to its own 100% so the mix can be compared by eye.
  const columnTotals = (stats.chart.categories || []).map((_category, index) =>
    counts.reduce((sum, data) => sum + (data[index] || 0), 0),
  );

  return OUTCOME_CHART_SERIES_KEYS.map((key, keyIndex) => ({
    name: translate(`hospital.analytics.appointment_outcomes.${key}`),
    data: counts[keyIndex].map((value, index) => {
      const total = columnTotals[index];
      // A service with nothing booked in the period stays at zero rather than dividing
      // by it -- it still gets a category on the axis, just an empty column.
      return total > 0 ? Number(((value / total) * 100).toFixed(1)) : 0;
    }),
    color: colorByKey[key],
  }));
}
